import { useEffect, useMemo, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import { Accordion, RhfSelect, RhfToggle } from '@core/components';
import { getEntityById, getLabelFromId } from '@core/utils/collection';
import type { Vestment } from '../../../api/vestment';
import { useAppContext } from '../../context/AppContext';
import { AlbSubtype } from '../../enum/albSubtype';
import { MaterialKind } from '../../enum/materialKind';
import { VestmentType } from '../../enum/vestmentType';
import { useCustomerById } from '../../hooks/useCustomerById';
import {
  ALB_BODY_COLOR_NAME,
  ALB_BODY_FABRIC_NAME,
  ALB_LACE_STYLE_OPTIONS,
  albDesignSection,
  albLaceOptionMatches,
  albLaceOptions,
  getAlbFeatures,
  getAlbLaceOptionLabel,
  getAlbLaceStyleLabel,
  getAlbSubtypeLabel,
  usesAlbBodyDefault,
} from '../../utils/alb';
import { filterFabricsByFlag } from '../../utils/fabric';
import {
  type AlbItemFormValues,
  type OrderItemDialogSlotProps,
  type OrderItemFormValues,
  formatOrderItemSizeSummary,
  getFormValuesCategory,
  getMaterialSummary,
  getVestmentsByCategory,
} from '../../utils/orderItem';
import { albSizeFields } from '../../utils/size';
import { CollapsibleField } from './CollapsibleField';
import { OrderItemSummaryRow } from './OrderItemDialog';
import { RhfCollarSelect } from './RhfCollarSelect';
import { RhfColorSelect } from './RhfColorSelect';
import { RhfDesignPicker } from './RhfDesignPicker';
import { RhfFabricSelect } from './RhfFabricSelect';
import { RhfMaterialSelect } from './RhfMaterialSelect';

function useAlbBodyDefault(
  methods: UseFormReturn<AlbItemFormValues>,
  vestment?: Vestment
) {
  const { fabrics } = useAppContext();
  const prevVestmentId = useRef(vestment?.id);

  useEffect(() => {
    const previousId = prevVestmentId.current;
    prevVestmentId.current = vestment?.id;
    if (!vestment || previousId === vestment.id) {
      return;
    }

    const albFeatures = getAlbFeatures(vestment.type);
    if (!albFeatures?.hasSubtypes && methods.getValues('subtype')) {
      methods.setValue('subtype', '', { shouldDirty: true });
    }
    if (!albFeatures?.collar && methods.getValues('collar')) {
      methods.setValue('collar', '', { shouldDirty: true });
    }
    if (
      vestment.type !== VestmentType.AlbV1Roman &&
      methods.getValues('withLace')
    ) {
      methods.setValue('withLace', false, { shouldDirty: true });
    }
    if (!usesAlbBodyDefault(vestment.type)) {
      return;
    }

    if (!methods.getValues('bodyFabric')) {
      const miyaki = filterFabricsByFlag(fabrics, 'isAlbBody').find(
        (fabric) => fabric.name.trim() === ALB_BODY_FABRIC_NAME
      );
      if (miyaki) {
        methods.setValue('bodyFabric', String(miyaki.id), {
          shouldDirty: true,
        });
      }
    }
    if (!methods.getValues('bodyColor')) {
      const fabric = getEntityById(fabrics, methods.getValues('bodyFabric'));
      const white = fabric?.colors.find(
        (color) => color.name.trim() === ALB_BODY_COLOR_NAME
      );
      if (white) {
        methods.setValue('bodyColor', String(white.id), { shouldDirty: true });
      }
    }
  }, [fabrics, methods, vestment]);
}

function AlbItemForm({
  methods: methodsProp,
  expandedSection,
  setExpandedSection,
  setPreviewTab,
}: OrderItemDialogSlotProps<OrderItemFormValues>) {
  const methods = methodsProp as UseFormReturn<AlbItemFormValues>;
  const { vestments } = useAppContext();

  const vestmentId = methods.watch('vestment');
  const subtype = methods.watch('subtype');
  const laceStyle = methods.watch('laceStyle');
  const designSection = albDesignSection(subtype);
  const prevSubtype = useRef(subtype);

  useEffect(() => {
    const previous = prevSubtype.current;
    prevSubtype.current = subtype;
    if (previous === subtype) {
      return;
    }
    const section = albDesignSection(subtype);
    if (!section?.design) {
      if (methods.getValues('design')) {
        methods.setValue('design', null, { shouldDirty: true });
      }
      if (methods.getValues('designCode')) {
        methods.setValue('designCode', '', { shouldDirty: true });
      }
    }
    if (!section?.thread && methods.getValues('thread')) {
      methods.setValue('thread', '', { shouldDirty: true });
    }
    if (subtype !== AlbSubtype.Lace) {
      if (methods.getValues('laceStyle')) {
        methods.setValue('laceStyle', '', { shouldDirty: true });
      }
      if (methods.getValues('laceOption')) {
        methods.setValue('laceOption', '', { shouldDirty: true });
      }
    }
  }, [methods, subtype]);

  const category = getFormValuesCategory(methods.getValues());
  const categoryVestments = useMemo(
    () => getVestmentsByCategory(vestments, category),
    [vestments, category]
  );
  const vestment = getEntityById(categoryVestments, vestmentId);
  const isRomanAlb = vestment?.type === VestmentType.AlbV1Roman;
  const showsCollar = getAlbFeatures(vestment?.type)?.collar ?? false;
  const showsLace =
    (getAlbFeatures(vestment?.type)?.hasSubtypes ?? false) &&
    subtype === AlbSubtype.Lace;

  useAlbBodyDefault(methods, vestment);

  return (
    <>
      <Accordion
        title="Body"
        expanded={expandedSection === 'body'}
        onChange={(expanded) => setExpandedSection(expanded ? 'body' : null)}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <RhfFabricSelect<AlbItemFormValues>
            name="bodyFabric"
            label="Fabric"
            flag="isAlbBody"
          />
          <RhfColorSelect<AlbItemFormValues>
            name="bodyColor"
            label="Color"
            fabricFieldName="bodyFabric"
          />
          {isRomanAlb && (
            <RhfToggle<AlbItemFormValues>
              name="withLace"
              label="With lace"
              outlined
            />
          )}
        </Box>
      </Accordion>

      <Accordion
        title="Design"
        expanded={expandedSection === 'design'}
        collapsed={!designSection?.design}
        onChange={(expanded) => setExpandedSection(expanded ? 'design' : null)}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <RhfDesignPicker
            vestmentType={vestment?.type}
            onValueChange={() => setPreviewTab('design')}
          />
          <CollapsibleField collapsed={!designSection?.thread}>
            <RhfMaterialSelect<AlbItemFormValues>
              name="thread"
              label="Thread"
              kind={MaterialKind.Thread}
            />
          </CollapsibleField>
        </Box>
      </Accordion>

      <Accordion
        title="Lace"
        expanded={expandedSection === 'lace'}
        collapsed={!showsLace}
        onChange={(expanded) => setExpandedSection(expanded ? 'lace' : null)}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <RhfSelect<AlbItemFormValues>
            name="laceStyle"
            label="Style"
            options={ALB_LACE_STYLE_OPTIONS}
            onValueChange={(value) => {
              const option = methods.getValues('laceOption');
              if (option && !albLaceOptionMatches(value, option)) {
                methods.setValue('laceOption', '', { shouldDirty: true });
              }
            }}
          />
          <CollapsibleField collapsed={!laceStyle}>
            <RhfSelect<AlbItemFormValues>
              name="laceOption"
              label="Lace"
              options={albLaceOptions(laceStyle)}
            />
          </CollapsibleField>
        </Box>
      </Accordion>

      <Accordion
        title="Collar"
        expanded={expandedSection === 'collar'}
        collapsed={!showsCollar}
        onChange={(expanded) => setExpandedSection(expanded ? 'collar' : null)}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <RhfCollarSelect<AlbItemFormValues>
            name="collar"
            label="Type"
            vestment={vestment}
            sx={{ flex: 1, width: { xs: '100%', md: 'auto' } }}
          />
          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
        </Box>
      </Accordion>
    </>
  );
}

function AlbItemSummary({
  methods: methodsProp,
}: OrderItemDialogSlotProps<OrderItemFormValues>) {
  const methods = methodsProp as UseFormReturn<AlbItemFormValues>;
  const { vestments, sizes, fabrics, colors, collars, threads } =
    useAppContext();

  const vestmentId = methods.watch('vestment');
  const subtype = methods.watch('subtype');
  const quantity = methods.watch('quantity');
  const sizeId = methods.watch('size');
  const forCustomerId = methods.watch('forCustomer');
  const length = methods.watch('length');
  const shoulder = methods.watch('shoulder');
  const sleeve = methods.watch('sleeve');
  const neck = methods.watch('neck');
  const chest = methods.watch('chest');
  const waist = methods.watch('waist');
  const figure = methods.watch('figure');
  const bodyFabricId = methods.watch('bodyFabric');
  const bodyColorId = methods.watch('bodyColor');
  const designCode = methods.watch('designCode');
  const threadId = methods.watch('thread');
  const laceStyle = methods.watch('laceStyle');
  const laceOption = methods.watch('laceOption');
  const withLace = methods.watch('withLace');
  const collarId = methods.watch('collar');

  const vestment = vestments.find((v) => String(v.id) === vestmentId);
  const albFeatures = getAlbFeatures(vestment?.type);
  const designSection = albDesignSection(subtype);
  const isRomanAlb = vestment?.type === VestmentType.AlbV1Roman;
  const { data: forCustomer } = useCustomerById(forCustomerId);

  return (
    <>
      <OrderItemSummaryRow label="Quantity" fadeKey={quantity}>
        <Typography variant="caption" color="text.secondary">
          {quantity ?? '-'}
        </Typography>
      </OrderItemSummaryRow>
      <OrderItemSummaryRow
        label="Size"
        fadeKey={`${vestment?.type}-${sizeId}-${forCustomerId}-${length}-${shoulder}-${sleeve}-${neck}-${chest}-${waist}-${figure}`}
      >
        <Typography variant="caption" color="text.secondary">
          {formatOrderItemSizeSummary(albSizeFields(vestment?.type), {
            sizeName: getLabelFromId(sizes, sizeId),
            size: getEntityById(sizes, sizeId),
            customer: forCustomer,
            vestmentType: vestment?.type,
            length,
            shoulder,
            sleeve,
            neck,
            chest,
            waist,
            figure,
          })}
        </Typography>
      </OrderItemSummaryRow>
      {albFeatures?.hasSubtypes && (
        <OrderItemSummaryRow label="Subtype" fadeKey={subtype}>
          <Typography variant="caption" color="text.secondary">
            {getAlbSubtypeLabel(subtype) ?? '-'}
          </Typography>
        </OrderItemSummaryRow>
      )}
      <OrderItemSummaryRow
        label="Body"
        fadeKey={`${bodyFabricId}-${bodyColorId}`}
        colorHex={getEntityById(colors, bodyColorId)?.hexCode}
      >
        <Typography variant="caption" color="text.secondary">
          {getLabelFromId(fabrics, bodyFabricId) ?? '-'} /{' '}
          {getLabelFromId(colors, bodyColorId) ?? '-'}
          {isRomanAlb ? ` ${withLace ? 'with' : 'without'} lace` : ''}
        </Typography>
      </OrderItemSummaryRow>
      {designSection?.design && (
        <OrderItemSummaryRow
          label="Design"
          fadeKey={`${designCode}-${threadId}`}
          colorHex={
            designSection.thread
              ? getEntityById(threads, threadId)?.hexCode
              : undefined
          }
        >
          <Typography variant="caption" color="text.secondary">
            {designCode || '-'}
            {designSection.thread
              ? ` / ${getMaterialSummary(threads, threadId) ?? '-'}`
              : ''}
          </Typography>
        </OrderItemSummaryRow>
      )}
      {subtype === AlbSubtype.Lace && (
        <OrderItemSummaryRow
          label="Lace"
          fadeKey={`${laceStyle}-${laceOption}`}
        >
          <Typography variant="caption" color="text.secondary">
            {[
              getAlbLaceStyleLabel(laceStyle),
              getAlbLaceOptionLabel(laceOption),
            ]
              .filter(Boolean)
              .join(' / ') || '-'}
          </Typography>
        </OrderItemSummaryRow>
      )}
      {albFeatures?.collar && (
        <OrderItemSummaryRow label="Collar" fadeKey={collarId}>
          <Typography variant="caption" color="text.secondary">
            {getLabelFromId(collars, collarId) ?? '-'}
          </Typography>
        </OrderItemSummaryRow>
      )}
    </>
  );
}

const AlbItemDialogContent = {
  renderForm: (slot: OrderItemDialogSlotProps<OrderItemFormValues>) => (
    <AlbItemForm {...slot} />
  ),
  renderSummary: (slot: OrderItemDialogSlotProps<OrderItemFormValues>) => (
    <AlbItemSummary {...slot} />
  ),
};

export default AlbItemDialogContent;
